import React, { useState } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import i18n from 'i18next'
import { formatRelative } from 'date-fns'
import Typography from '../../../../../UniformComponents/Typography'
import Button from '../../../../../UniformComponents/Button'
import Tooltip from '../../../../../UniformComponents/Tooltip'
import Badge from '../../../../../UniformComponents/Badge'
import BubbleLoader from '../../../../../UniformComponents/BubbleLoader'
import analysingData from './analysingData.svg'
import AIChat from './AIChat'
import StatusBadge from '../../../../Survey/components/StatusBadge'
import './aiAssistant.scss'

const AiSummaryNotReady = () => (
    <div className="not-ready">
        <img src={analysingData} alt="Analysing data" />
        <Typography type="p">{i18n.t('s_report_ai_summary_not_ready')}</Typography>
        <div className="generating-container">
            <BubbleLoader />
        </div>
    </div>
)

const AiAssistant = observer(({ reportStore }) => {
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(true)
    const [showChat, setShowChat] = useState(false)

    const {
        showAiAssistant,
        toggleShowAiAssistant,
        copyAiSummary,
        refreshAiSummary,
        AiSummary,
        summaryLastUpdated,
        summaryNumberOfResponses,
        generatingSummary,
    } = reportStore

    const generatedKeys = Object.keys(R.pathOr([], ['keyFindings'], AiSummary))
        .filter((kf) => !['KPI Summary', 'Recommendations', 'Summary'].includes(kf))

    const scrollTo = (e) => {
        try {
            const pageQuestionId = e.target.getAttribute('pageQuestionId')
            document.getElementById(`questionTitle_${pageQuestionId.toUpperCase()}`).scrollIntoView({ block: 'center', behavior: 'smooth' })
        } catch (err) {
            console.log(err)
        }
    }

    const replaceInsightMergefield = (insight) => {
        reportStore.exportInit()
        const pageQuestionIds = insight.match(/[a-zA-Z0-9\-]{36}/gm)
        if (!pageQuestionIds) return insight

        const relatedQuestions = []

        pageQuestionIds.forEach((pqid) => {
            const question = reportStore.questions.find((q) => q.pageQuestionId.toLowerCase() === pqid)

            if (!question) return

            relatedQuestions.push({
                id: pqid,
                title: question.questionTitle,
            })
        })

        return (
            <>
                {insight.replace(/\[.+\]/gm, '').replace(' .', '.')}
                {relatedQuestions.length
                    ? (
                        <>
                            {' ['}
                            {relatedQuestions.map(({ id, title }, i) => (
                                <>
                                    <a
                                        className="scrollToQuestionAi"
                                        pageQuestionId={id}
                                        onClick={scrollTo}>
                                        {title}
                                    </a>
                                    {i !== (relatedQuestions.length - 1) && ', '}
                                </>
                            ))}
                            {']'}
                        </>
                    )
                    : null}
            </>
        )
    }

    const insightsHeader = () => {
        const { summaryStatus, appStore } = reportStore
        const { userData: { role } } = appStore
        const isReadOnlyUser = role >= 150

        const refreshBtn = (
            <Button
                title={role >= 150 ? null : i18n.t('s_report_ai_refresh')}
                icon="fas fa-refresh"
                onClick={refreshAiSummary}
                cssClass="icon-action-btn"
                disabled={role >= 150}
            />
        )

        return (
            <div className="insights-header">
                <div className="insights-row">
                    <Typography>{i18n.t('s_edit_form_survey_status')}:</Typography>
                    {process.env.SURVEY_STATE_REBRAND_2026 ? (
                        <StatusBadge status={appStore.surveyStatus} />
                    ) : (
                        <Badge
                            type={reportStore.surveyStatus.style}
                            label={reportStore.surveyStatus.label}
                            icon={reportStore.surveyStatus.icon}
                        />
                    )}
                </div>
                <div className="insights-row">
                    <Typography>{i18n.t('report.aiSummary.status.label')}:</Typography>
                    <Badge
                        isRounded
                        type={summaryStatus?.style}
                        label={summaryStatus?.label}
                        style={{ position: 'relative' }}
                        hoverInfo={{
                            desc: summaryStatus?.description,
                            dir: 'bottom-left',
                        }}
                    />
                </div>
                <div className="insights-row">
                    {summaryLastUpdated ? (
                        <Typography type="tiny" color="grey">
                            {i18n.t('s_report_last_updated',
                                { date: formatRelative(new Date(summaryLastUpdated), new Date()) })}
                            {' '}
                            (n = {summaryNumberOfResponses})
                        </Typography>
                    ) : <div />}
                    <div className="insights-actions">
                        { isReadOnlyUser
                            ? (
                                <Tooltip
                                    text={i18n.t('report.verbatim.aiCoding.generate.insufficientRights')}
                                    direction="left"
                                    isNew
                                >
                                    {refreshBtn}
                                </Tooltip>
                            ) : refreshBtn}
                        <Button
                            title={i18n.t('copy')}
                            icon="far fa-copy"
                            onClick={copyAiSummary}
                            cssClass="icon-action-btn"
                        />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div id="ai-assistant-container">
            <Button
                icon={`fal ${showAiAssistant ? 'fa-times' : 'fa-sparkles'}`}
                cssClass={`regular secondary ai-toggle-button ${showAiAssistant ? 'expanded' : ''}`}
                onClick={toggleShowAiAssistant}
            />

            <div className={`ai-panel ${showAiAssistant ? 'slide-in' : ''}`}>
                <div className="ai-panel-header">
                    <i className="fal fa-sparkles" />
                    <Typography type="h3">{i18n.t('s_report_ai_assistant')}</Typography>
                    <button
                        style={{ width: 18, height: 18, backgroundColor: 'transparent', border: 0 }}
                        onClick={() => setShowChat(!showChat)}
                        cssClass="icon-action-btn"
                    />
                </div>

                <div className="ai-panel-content">
                    {generatingSummary ? (
                        <AiSummaryNotReady />
                    ) : (
                        <>
                            <div className="section-header">
                                <div className="section-title">
                                    <i className="fal fa-sparkles" />
                                    <Typography type="h4">{i18n.t('s_report_ai_summary')}</Typography>
                                </div>
                                <Button
                                    icon={`fas fa-chevron-${isSummaryExpanded ? 'up' : 'down'}`}
                                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                                    cssClass="icon-action-btn"
                                />
                            </div>

                            {(!AiSummary && isSummaryExpanded) ? (
                                <div className="section-body">
                                    <div className="section-item">
                                        {insightsHeader()}
                                        <div className="insights-body">
                                            <Typography type="tiny">{i18n.t('s_report_no_summary_explainer')}</Typography>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {(isSummaryExpanded && AiSummary) ? (
                                <div className="section-body">
                                    <div className="section-item">
                                        {insightsHeader()}
                                        {R.pathOr([], ['keyFindings', 'KPI Summary'], AiSummary).length ? (
                                            <div className="insights-body">
                                                <Typography type="b">{i18n.t('s_report_ai_kpi')}</Typography>
                                                {R.pathOr([], ['keyFindings', 'KPI Summary'], AiSummary).map((insight) => (
                                                    <div className="insight-content recommendations-body">
                                                        <ul>
                                                            <li>
                                                                <Typography type="tiny">
                                                                    {replaceInsightMergefield(insight)}
                                                                </Typography>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {R.pathOr([], ['keyFindings', 'Summary'], AiSummary).length ? (
                                            <div className="insights-body recommendations-body">
                                                <Typography type="b">{i18n.t('s_report_summary')}</Typography>
                                                {R.pathOr([], ['keyFindings', 'Summary'], AiSummary).map((insight) => (
                                                    <div className="insight-content recommendations-body">
                                                        <ul>
                                                            <li>
                                                                <Typography type="tiny">
                                                                    {replaceInsightMergefield(insight)}
                                                                </Typography>
                                                            </li>
                                                        </ul>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="section-item">
                                        <Typography type="b">{i18n.t('s_report_ai_top_findings')}</Typography>
                                        <div className="findings-body">
                                            {generatedKeys.map((gk) => (
                                                <div className="findings-content recommendations-body">
                                                    <Typography type="b">{gk}</Typography>

                                                    <ul>
                                                        {AiSummary.keyFindings[gk].map((f) => (
                                                            <li>
                                                                <Typography type="tiny">
                                                                    {replaceInsightMergefield(f)}
                                                                </Typography>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="section-item">
                                        <Typography type="b">{i18n.t('s_report_ai_recommendations')}</Typography>
                                        <div className="recommendations-body">
                                            <ul>{R.pathOr([], ['Recommendations'], AiSummary.keyFindings).map((rec) => <li><Typography type="tiny">{replaceInsightMergefield(rec)}</Typography></li>)}</ul>
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                        </>
                    )}
                </div>
                {process.env.USE_AI_CHAT && showChat && <AIChat reportStore={reportStore} />}
                <div className="disclaimer">
                    <Typography type="tiniest">{i18n.t('s_report_ai_disclaimer')}</Typography>
                </div>
            </div>
        </div>
    )
})

export default AiAssistant
